import { useCallback, useSyncExternalStore } from "react";
import type { MemorySliceStore } from "../MemorySliceStore";
import type { MemorySliceDescriptor, MemorySliceTuple } from "../types";
import { useLayoutRef } from "./useLayoutRef";

/**
 * Subscribes to a memory slice with full-value updates.
 *
 * @typeParam TValue Slice value type.
 * @param store Memory slice store instance.
 * @param slice Slice descriptor (stable reference from slice module).
 * @returns Tuple `[value, setValue]` compatible with `useState` updaters.
 */
export const useMemorySlice = <TValue>(
    store: MemorySliceStore,
    slice: MemorySliceDescriptor<TValue>,
): MemorySliceTuple<TValue> => {
    const storeRef = useLayoutRef( store );
    const sliceRef = useLayoutRef( slice );

    const subscribe = useCallback( ( listener: () => void ) => {
        return storeRef.current.subscribe( sliceRef.current, listener );
    }, [ sliceRef, storeRef ] );

    const getSnapshot = useCallback( () => {
        return storeRef.current.getSnapshot( sliceRef.current );
    }, [ sliceRef, storeRef ] );

    const value = useSyncExternalStore( subscribe, getSnapshot, getSnapshot );

    const setValue = useCallback(
        ( next: TValue | ( ( previous: TValue ) => TValue ) ) => {
            store.setValue( slice, next );
        },
        [ store, slice ],
    );

    return [ value, setValue ];
};
